import { Environment } from "@fruugo/utilities";
import { ButtonProps } from "./modules/components/Buttons/Button";
import { LinkButtonProps } from "./modules/components/Buttons/LinkButton";

declare global {
  interface Window {
    // Globals
    currentCountry: string;
    currentCountryName: string;
    currentLang: string;
    currentCountryDomain: string;
    currentCurrencyCode: string;
    geoIPCountry: string;
    messagePropertiesResourceUrl: string;
    pageUrls: PageUrls;
    environment: Environment;
    totalCartItems: number;
    pageType?: string; // Used for checking page type in code
    pageTitle?: string; // Used for Analytics, always english
    rtl: boolean;
    voucher?: Voucher;
    GAKey: string;
    klarnaSettings: {
      paymentMethodAvailable: boolean;
      onSiteMessagingClientId: string;
    };
    resourceUrls: {
      iconSpriteSheet: string;
      flagSpriteSheet: string;
    };
    hotjarSiteId: string;
    botInformation: string;
    enableEnergyLabels: boolean;
    // GA globals
    _gre: unknown[];
    // Product Page
    breadcrumbs?: Link[];
    attributeDefinitions?: Attribute[];
    cheapestShippingMethod?: SkuShippingMethod;
    // Cart Page
    shippingGroups?: ShippingGroup[];
    // Help Page
    helpCentreJsUrls?: string[];
    helpCentreCssUrls?: string[];
    // Search Page
    searchQuery?: string;
    // RTB House Advertising Tag
    rtbhEvents: unknown[];
    // Pinterest tracking
    pintrk?: (key: string, value: unknown, options?: unknown) => unknown;
    // TikTok Pixel
    ttq?: {
      grantConsent: () => void;
      revokeConsent: () => void;
      track: (eventName: string, data: Record<string, unknown>) => void;
    };
  }

  // Stop using this approach, just add an optional property to the global window above instead
  interface ProductPage extends Window {
    skuId: string;
    productId: string;
    descriptionBeforeTranslation: ContentBeforeTranslation;
    skuInfo: Sku;
    dutiesPriceBreakdown: DutiesPriceBreakdown | null;
    machineTranslationRequirements: {
      title: boolean;
      description: boolean;
      safetyAttributes: boolean;
    };
    retailerInformation: RetailerInformation;
    localPlusShippingFeature: LocalPlusShippingFeature;
    productTags: ProductTag[];
  }

  interface SearchPage extends Window {
    searchInformation: SearchInformation;
  }

  interface WarehouseCategoryPage extends Window {
    warehouseCategoryNames: string[];
  }

  // Global functions for handling image errors
  function handleImageError(target: EventTarget | null): void;
  function handleEnergyLabelImageError(target: EventTarget | null): void;
}

type ContentBeforeTranslation = {
  enabled: boolean;
  languageText: string;
  language: string;
};

type PageUrls = {
  homeUrl: string;
  helpCentreUrl: string;
  accountUrl: string;
  cartUrl: string;
  privacyPolicyUrl: string;
  whereIsMyOrderUrl: string;
};

export enum DeviceWidth {
  Small = 576,
  Medium = 768,
  Large = 992,
  XLarge = 1280,
  XXLarge = 1600,
}

type Link = {
  href: string;
  text: string;
};

type MenuDataMeta = {
  country: string;
  language: string;
  requestTime: string;
  requestUrl: string;
};

export type MenuDataItem = {
  id: string;
  items: MenuDataItem[];
  link: Link;
  type: MenuDataItemType;
};

export type MenuData = {
  items: MenuDataItem[];
  meta: MenuDataMeta;
};

enum MenuDataItemType {
  Department = "department",
  Category = "category",
}

export type NavigationHeaderMeta = {
  requestUrl: string;
  requestTime: string;
  country: string;
  language: string;
};

export type NavigationItemType = "brand" | "category" | "department";

export type NavigationItem = {
  id: string;
  link: Link;
  type: NavigationItemType;
  items: NavigationItem[];
};

export type NavigationHeaderResponse = {
  meta: NavigationHeaderMeta;
  items: NavigationItem[];
};

export type SearchRecommendation = {
  id: string;
  level: number;
  link: string;
  name: string;
  type: SearchRecommendationType;
};

export enum SearchRecommendationType {
  Warehouse = "WAREHOUSE",
  Webshop = "WEBSHOP",
  QuerySuggestion = "QUERY_SUGGESTION",
}

export type SearchInformation = {
  searchQuery: string;
  queryParameters: string;
  queryResultCount: number;
  queryCategoryFilters: string[];
  queryFacetFields: string[];
  queryTimestamp: string;
  querySortParameters: string;
  queryResponseTime: string;
};

export type Voucher = {
  discountPercentage: number;
  validityEnd: number;
};

export type CarouselResponse = {
  title: string;
  productTiles: ProductTile[];
};

export type ProductTile = {
  imageUrl: string;
  title: string;
  price: string;
  normalPrice: string;
  priceDiscount: string;
  productPageLink: Link;
  branded: boolean;
  productId: string;
  category: string;
  brand: string;
  numericTotalDiscount: number;
  numericTotalForAnalytics: number;
  eurNumericTotalForAnalytics: number;
  englishCategories: string[];
  originalTitle: string;
  uniqueSellerCount: number;
  otherSellersLinkEnabled: boolean;
};

type GTIN = {
  type: string;
  value: string;
  title: string;
};

type SkuPrice = {
  totalInclTax: string;
  numericTotalInclTax: number;
  numericTotalExclTax: number;
  numericTotalDiscount: number | null;
  originalInclTax: string;
  totalInclTaxAfterDiscount: string | null;
  numericTotalInclTaxAfterDiscount: number | null;
  numericTotalExclTaxAfterDiscount: number | null;
  tax: string;
  taxAfterDiscount: string;
  currencyCode: string;
  eurValueForAnalytics: string | null;
  usdValueForAnalytics: string;
  voucherDiscount: string | null;
  totalDiscount: string | null;
  numericDiscountPercentage: number | null;
  discountPercentage: string | null;
  taxCalculationRequiredInCheckout: boolean;
  merchantDiscountApplied: boolean;
  voucherDiscountApplied: boolean;
};

type DutiesPriceBreakdown = {
  itemPrice: string;
  dutiesAndFees: string;
  tax: string;
  taxRate: string;
  total: string;
};

type SkuAttribute = {
  id: string;
  type: string;
  value: string;
  title: string;
  description: string;
  facet: boolean;
  variant: boolean | null;
};

type SkuDelivery = {
  earliest: string;
  latest: string;
  range: string;
  exceedsDeliveryTimescales: boolean;
};

export type SkuShippingMethod = {
  type: string;
  title: string;
  description: string;
  carrierName: string;
  serviceName: string;
  price: string;
  numericPrice: number;
  shippingExcludingFeeFormatted: string;
  deliveryDates: SkuDelivery;
  currencyCode: string;
  internalId: number;
  internalName: string;
  daysToDelivery: number;
  shippingTypeDeliveryDaysMin: number;
  shippingTypeDeliveryDaysMax: number;
  free: boolean;
  express: boolean;
  shippingType: ShippingType;
};

/** CX-2571: the buy box view of the API offer (marketplace QuantityOfferView). Customer currency. */
export type QuantityOffer = {
  currentQuantity: number;
  targetQuantity: number;
  free: boolean;
  currencyCode?: string;
  saving: number;
  shippingAtCurrent: number;
  shippingAtTarget: number;
  unitPriceAtCurrent: number;
  unitPriceAtTarget: number;
};

export type SkuShipping = {
  dispatchWithin: string;
  dispatchLatest: string;
  methods: SkuShippingMethod[];
  originCountryCode: string;
  destinationCountryCode: string;
  daysToDispatch: number;
  cheapestShippingMethod?: SkuShippingMethod;
};

type ManufacturerContact = {
  type: string;
  legalName: string;
  tradingName: string;
  phoneNumber: string;
  email: string;
  url: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  postalCode: string;
  country: string;
};

type WarehouseCategory = {
  facetingNameList: string[];
};

export type Sku = {
  id: number;
  productId: number;
  gtin: GTIN;
  title: string | null;
  titleBeforeTranslation: string;
  description: string;
  descriptionBeforeTranslation: string;
  machineTranslationOfTitleAllowed: boolean;
  originalDescriptionLanguage: string;
  price: SkuPrice;
  sellable: true;
  sellableQuantity: number;
  availabilityStatus: string;
  brand: {
    title: string;
    branded: boolean;
  };
  attributes: SkuAttribute[];
  images: {
    urls: string[];
    urlSmall: string;
  };
  shipping: SkuShipping;
  facetingCategoryCode: string;
  manufactureContacts: ManufacturerContact[];
  premiumSeller: boolean;
  storefrontLogo?: string;
  storefrontBanner?: string;
  retailerVRN?: string;
  retailerId: number;
  retailerName: string;
  retailerCountry?: string;
  retailerCurrency?: string;
  companyId?: string;
  displayPrice: string;
  numericDisplayPrice: number;
  numericTotalDisplayPrice: number;
  condition: "REFURBISHED" | "NEW";
  analyticsData: {
    // Values Always in English
    title: string;
    warehouseCategory: WarehouseCategory;
    grade: string;
    condition: string;
  };
  shippingAmount: number;
  shippingHandlingChargeAmount: number;
  shippingDutyAmount: number;
};

export type Product = {
  quantity: number;
  skuId?: number;
  sku: Sku;
  skuUrl?: string;
  totalItemsPrice?: string;
  amountSavedPerItem?: string;
  totalItemsAmountSaved?: string;
  itemAvailabilityMessage?: string;
  taxCountry?: string;
  totalVoucherDiscount?: string;
  warehouseCategoryEnglish?: WarehouseCategory;
};

export interface GAEcommerceEventPayload {
  id: number;
  name?: string;
  brand?: string;
  quantity?: number;
  price?: string;
  list_name?: string;
  list_position?: number;
  isCarousel?: boolean;
  category?: string;
}

export interface AttributeDetail {
  values: string[];
  title: string;
}

export type SafetyAttributes = {
  [key in string]: AttributeDetail;
};

export type SafetyAttributeResponse = {
  attributes: SafetyAttributes;
};

export type IconType =
  | "help"
  | "translate"
  | "verified"
  | "location"
  | "storefront"
  | "account"
  | "cart"
  | "home"
  | "grid"
  | "package"
  | "quiz"
  | "back-arrow"
  | "category"
  | "sell"
  | "close"
  | "delivery-truck-speed"
  | "tag"
  | "find-in-page"
  | "acute"
  | "chevron-left"
  | "chevron-right"
  | "chevron-down"
  | "chevron-up"
  | "search"
  | "back-arrow"
  | "redeem"
  | "package-2"
  | "page-info"
  | "timer"
  | "eye-tracking"
  | "info"
  | "trophy"
  | "sunny"
  | "flare"
  | "diamond-shine"
  | "eye-tracking";

export type FlagCountry =
  | "AE"
  | "AT"
  | "AU"
  | "BE"
  | "BH"
  | "CA"
  | "CH"
  | "CN"
  | "CZ"
  | "DE"
  | "DK"
  | "EG"
  | "ES"
  | "FI"
  | "FR"
  | "GB"
  | "GR"
  | "HU"
  | "IE"
  | "IL"
  | "IN"
  | "JP"
  | "KR"
  | "IT"
  | "LT"
  | "LU"
  | "LV"
  | "MY"
  | "NL"
  | "NO"
  | "NZ"
  | "PH"
  | "PL"
  | "PT"
  | "QA"
  | "RO"
  | "RU"
  | "SA"
  | "SE"
  | "SG"
  | "SK"
  | "TR"
  | "US"
  | "ZA";

export interface RetailerInformation {
  id: string;
  name: string;
  premiumSeller?: boolean;
  inRegionShipping?: boolean;
  addressLine?: string;
  companyNumber?: string;
  vrn?: string;
  daysToDispatch?: number;
  shippingFromCountryCode?: string;
  shippingFromCountryName?: string;
  shopUrl?: string;
  bannerImageUrl?: string;
  badgeImageUrl?: string;
  country?: string;
}

export type CartResponse = {
  cartValid: boolean;
  success: boolean;
  checkoutUrl: string;
  totalItemsInCart: number;
  cartId: number;
  message: string;
};

export type CommonResponse = {
  success: boolean;
  message: string;
};

export type AddToCartResponse = CommonResponse & {
  checkoutUrl: string;
  totalItemsInCart: number;
  cartValid: boolean;
  cartId: number | null;
  quantity: number;
  addedCartItem: CartItemView | null;
};

export type CartItemView = {
  skuId: number;
  productId: number | null;
  title: string | null;
  imageUrlSmall: string | null;
  eprelId: string;
  price: {
    formattedDisplayPrice: string | null;
    formattedRrp: string | null;
    formattedDiscountPercentage: string | null;
    discounted: boolean;
    taxCalculationRequiredInCheckout: boolean;
  };
  shipping: {
    freeShippingAvailable: boolean;
  };
};

export type Attribute = {
  type: string;
  name: string;
  displayName: string;
  id: string;
  language: string;
  values: string[];
  skuIdsByValue: Record<string, number[]>;
  nonSelectableValues: string[];
  selectedValue?: string;
};

export type ShippingGroup = {
  cartItems: CartItem[];
  retailer: RetailerShippingInfo;
  shippingMethod: ShippingMethod;
};

export type CartItem = {
  quantity: number;
  skuId: number;
  sku: Sku | null;
  skuUrl: string | null;
  totalItemsPrice: string | null;
  amountSavedPerItem: string | null;
  totalItemsAmountSaved: string | null;
  itemAvailabilityMessage: string | null;
  taxCountry: string | null;
  totalVoucherDiscount: string | null;
};

export type ShippingMethod = {
  canBeApplied: boolean;
  daysToDelivery: number;
  description: string;
  estimatedDeliveryDateRange: string;
  free: boolean;
  freeShippingLimit: number | null;
  fruugoOffered: boolean;
  getShippingVATs: ShippingVAT[];
  internalId: number;
  internalName: string;
  language: string;
  name: string;
  originCountryCode: string;
  shippingTypeDeliveryDaysMax: number;
  shippingTypeDeliveryDaysMin: number;
  type: string | null;
  vatAmount: string;
  vatFreePrice: string;
  vatIncludedPrice: string;
  vatPercentage: string;
};

type ShippingVAT = {
  taxBracket: string;
  weight: string;
  vatFreePrice: string;
};

export type RetailerShippingInfo = {
  country: string;
  currency: string;
  id: string;
  name: string;
};

export type ImageSizes = {
  alt: string;
  format: "jpg" | "png" | "webp";
  height: number;
  src: string;
  width: number;
};

export type HomepageContent = {
  theme: string;
  items: HomepageContentItem[];
};

export type HomepageContentItem = {
  id: number;
  imageSizes: ImageSizes[];
  imageUrl: string;
  subtitle: string;
  title: string;
  cta: {
    text: string;
    href: string;
  };
};

type LocalPlusShippingFeature = {
  regionType: RegionType;
};

export enum RegionType {
  WithinSameCountry = "WITHIN_SAME_COUNTRY",
  WithinLocalRegion = "WITHIN_LOCAL_REGION",
  WithinEU = "WITHIN_EU",
}

export type ShippingType = "FREE" | "EXPRESS" | "STANDARD";

export type ItemListContext = {
  item_id: string;
  item_list_name: string;
  item_list_id: string;
};

export type ProductTagContent = {
  title: string;
  description: string;
  icon: {
    name: IconType;
    fill: string;
  };
};

// Using typecasting instead of an enum.
export const ProductTagTypeObject = {
  FreeShipping: "FREE_SHIPPING",
  ExpressShipping: "EXPRESS_SHIPPING",
  LowStock: "LOW_STOCK",
} as const;

export type ProductTagType =
  (typeof ProductTagTypeObject)[keyof typeof ProductTagTypeObject];

type ProductTag = {
  productTagType: ProductTagType;
  values: string[];
};

export type GAListInfo = {
  listId?: string;
  listName?: string;
  listCategory?: string;
  searchTerm?: string;
};

export type CountryOption = {
  country: string;
  localisedDisplayName: string;
  currency?: string;
  currencySymbol?: string;
};

export type LanguageOption = {
  language: string;
  localisedDisplayName: string;
  rightToLeft?: boolean;
  messagesUrl?: string;
};

export type CountryLanguageResponse = {
  countries: CountryOption[];
  languages: LanguageOption[];
};

export const buttonVariants = ["outline", "solid"] as const;

export type Variant = (typeof buttonVariants)[number];

export type ButtonWrapperProps = ButtonProps & { variant?: Variant };
export type LinkButtonWrapperProps = LinkButtonProps & { variant?: Variant };

export type Grade = "FAIR" | "GOOD" | "EXCELLENT" | "PREMIUM";

export type GradeConfig = {
  id: Grade;
  icon: IconType;
  title: string;
  previewText: string;
  remainingText: string;
};

export type RecommendationLinksResponse = {
  titleLink: Link;
  recommendedLinks: FilterOption[];
  seeAllLink: Link;
};

export type FilterOption = {
  link: Link;
  count: number;
  image: ImageType;
};

export type ImageType = {
  src: string;
  width: number;
  height: number;
  alt: string;
  format: ImageFormat;
};

export type ImageFormat = "JPG" | "WEBP" | "PNG";
